// ═══════════════════════════════════════════════════════════════════════════
// DISTIL PROTOCOL - WHITELIST MANAGER
// Manages user's whitelist of safe objects to hide from cleanup
// ═══════════════════════════════════════════════════════════════════════════

const WHITELIST_STORAGE_KEY = 'objectWhitelist'; // CHANGED: Now matches WhitelistPanel

// ═══════════════════════════════════════════════════════════════════════════
// WHITELIST MANAGEMENT
// ═══════════════════════════════════════════════════════════════════════════

/**
 * Get user's whitelist from localStorage
 * @returns {Object} Object mapping objectId -> metadata
 */
export function getWhitelist() {
  try {
    const stored = localStorage.getItem(WHITELIST_STORAGE_KEY);
    return stored ? JSON.parse(stored) : {};
  } catch (error) {
    console.error('Error loading whitelist:', error);
    return {};
  }
}

/**
 * Add an object to the whitelist
 * @param {string} objectId - Object ID to whitelist
 * @param {string} reason - Reason for whitelisting
 * @returns {boolean} Success status
 */
export function addToWhitelist(objectId, reason = 'Marked as safe via cleanup panel') {
  try {
    const whitelist = getWhitelist();
    
    if (!whitelist[objectId]) {
      whitelist[objectId] = {
        timestamp: Date.now(),
        addedBy: 'user',
        reason: reason
      };
      localStorage.setItem(WHITELIST_STORAGE_KEY, JSON.stringify(whitelist));
      return true;
    }
    
    return false; // Already whitelisted
  } catch (error) {
    console.error('Error adding to whitelist:', error);
    return false;
  }
}

/**
 * Add an object to the whitelist WITH display metadata.
 * Stores name, symbol, iconUrl, coinType so WhitelistPanel can display
 * correctly after page refresh without needing live wallet data.
 * @param {string} objectId - Object ID to whitelist
 * @param {string} reason - Reason for whitelisting
 * @param {Object} meta - Display metadata { name, symbol, iconUrl, coinType, rawBalance, decimals }
 * @returns {boolean} Success status
 */
export function addToWhitelistWithMeta(objectId, reason, meta = {}) {
  try {
    const whitelist = getWhitelist();
    if (!whitelist[objectId]) {
      whitelist[objectId] = {
        timestamp: Date.now(),
        addedBy: 'user',
        reason: reason,
        // Display metadata — survives page refresh
        name: meta.name || null,
        symbol: meta.symbol || null,
        iconUrl: meta.iconUrl || null,
        coinType: meta.coinType || null,
        rawBalance: meta.rawBalance || 0,
        decimals: meta.decimals ?? 9,
      };
      localStorage.setItem(WHITELIST_STORAGE_KEY, JSON.stringify(whitelist));
      return true;
    }
    return false;
  } catch (error) {
    console.error('Error adding to whitelist with meta:', error);
    return false;
  }
}

/**
 * Remove an object from the whitelist
 * @param {string} objectId - Object ID to remove
 * @returns {boolean} Success status
 */
export function removeFromWhitelist(objectId) {
  try {
    const whitelist = getWhitelist();
    
    if (whitelist[objectId]) {
      delete whitelist[objectId];
      localStorage.setItem(WHITELIST_STORAGE_KEY, JSON.stringify(whitelist));
      return true;
    }
    
    return false; // Not in whitelist
  } catch (error) {
    console.error('Error removing from whitelist:', error);
    return false;
  }
}

/**
 * Check if an object is whitelisted
 * @param {string} objectId - Object ID to check
 * @returns {boolean} True if whitelisted
 */
export function isWhitelisted(objectId) {
  const whitelist = getWhitelist();
  return !!whitelist[objectId];
}

/**
 * Filter out whitelisted objects from a list
 * @param {Array} objects - Array of objects to filter
 * @returns {Array} Filtered array without whitelisted objects
 */
export function filterWhitelisted(objects) {
  const whitelist = getWhitelist();
  return objects.filter(obj => {
    const objectId = obj?.data?.objectId || obj?.objectId;
    const isInWhitelist = !!whitelist[objectId];
    if (isInWhitelist) {
    }
    return !isInWhitelist;
  });
}

/**
 * Clear entire whitelist (use with caution)
 * @returns {boolean} Success status
 */
export function clearWhitelist() {
  try {
    localStorage.setItem(WHITELIST_STORAGE_KEY, '{}');
    return true;
  } catch (error) {
    console.error('Error clearing whitelist:', error);
    return false;
  }
}

/**
 * Get whitelist count
 * @returns {number} Number of whitelisted objects
 */
export function getWhitelistCount() {
  const whitelist = getWhitelist();
  return Object.keys(whitelist).length;
}

/**
 * Get whitelist as array (for display)
 * @returns {Array} Array of {id, ...metadata} objects
 */
export function getWhitelistArray() {
  const whitelist = getWhitelist();
  return Object.entries(whitelist).map(([id, data]) => ({
    id,
    ...data
  }));
}

/**
 * Export whitelist (for backup)
 * @returns {string} JSON string of whitelist
 */
export function exportWhitelist() {
  const whitelist = getWhitelist();
  return JSON.stringify(whitelist, null, 2);
}

/**
 * Import whitelist (from backup)
 * @param {string} jsonString - JSON string of whitelist
 * @returns {boolean} Success status
 */
export function importWhitelist(jsonString) {
  try {
    const whitelist = JSON.parse(jsonString);
    
    if (typeof whitelist === 'object' && whitelist !== null) {
      localStorage.setItem(WHITELIST_STORAGE_KEY, JSON.stringify(whitelist));
      return true;
    }
    
    return false;
  } catch (error) {
    console.error('Error importing whitelist:', error);
    return false;
  }
}

export default {
  getWhitelist,
  addToWhitelist,
  removeFromWhitelist,
  isWhitelisted,
  filterWhitelisted,
  clearWhitelist,
  getWhitelistCount,
  getWhitelistArray,
  exportWhitelist,
  importWhitelist,
};